import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, DownloadIcon, MapPin } from 'lucide-react';
import { PageBanner } from '../components/PageBanner';
import { SoutheastAsiaMap } from '../components/SoutheastAsiaMap';
import { AnimatedSection } from '../components/AnimatedSection';
import { PhotoCredit } from '../components/PhotoCredit';
const organizations = [{
  name: "M'Lop Tapang",
  location: 'Cambodia',
  logo: 'https://cdn.magicpatterns.com/uploads/o4iNnjJVqpskBbtmarvsyk/pasted-image.webp',
  url: 'https://www.mloptapang.org/',
  description: "M'Lop Tapang in Sihanoukville, Cambodia, serves over 5,000 children, youth, and families through integrated services delivered by multidisciplinary teams. Like TCI, they prioritize keeping children with families, operate a 24-hour emergency hotline, and train community members to recognize and respond to risk. Their staff is 98% Cambodian, with deep roots in the communities they serve."
}, {
  name: 'Mith Samlanh',
  location: 'Cambodia',
  logo: 'https://cdn.magicpatterns.com/uploads/9t4qxiTE8YKLF7aBfRCmCg/Mith-Samlanh-Logo.png',
  url: 'https://www.mithsamlanh.org/whoweare.php?=aboutus',
  description: 'Mith Samlanh delivers an integrated continuum of care for street-connected children, youth and families across Phnom Penh, moving them from crisis to stability to self-reliance. Outreach and drop-in services meet immediate protection and harm-reduction needs, while education, vocational training with job placement and transitional homes with family reintegration and foster care build pathways to dignified, independent futures.'
}, {
  name: 'Peuan Peuan',
  location: 'Thailand',
  logo: 'https://cdn.magicpatterns.com/uploads/rSC9sP2QtiFx63Qs8g7qAp/Clip_path_group-4.png',
  url: 'https://friends-international.org/peuan-peuan/',
  description: 'Peuan Peuan, works with vulnerable Thai and migrant children, youth, and families in Bangkok and Pattaya. Its outreach teams identify children at risk and provide case management, child protection support, and referrals to public education, healthcare, vocational training and social services as well as protection and family reintegration for migrant children ailing from bordering countries.'
}, {
  name: 'Baan Dek Foundation',
  location: 'Thailand',
  logo: 'https://cdn.magicpatterns.com/uploads/nuaodW4ZMkGrvMHkQXTPx7/Clip_path_group-5.png',
  url: 'https://www.baandekfoundation.org/',
  description: 'Baan Dek Foundation in Thailand focuses on migrant children in construction site camps—an estimated 60,000 children living in precarious conditions with limited access to services. Their model parallels ours: specialized case management, peer educators trained within communities, and systemic engagement with construction companies to improve conditions sector-wide.'
}, {
  name: 'Stairway Foundation',
  location: 'Philippines',
  logo: 'https://cdn.magicpatterns.com/uploads/vekhDhrMTRHY8ecHtfFeYM/Capture_edited.png',
  url: 'https://stairwayfoundation.org/',
  description: 'Stairway Foundation runs a Learning and Resource Center in Puerto Galera combining direct services with advocacy against child sexual abuse and online exploitation, in partnership with local and national government agencies and Break the Silence, a 45-member network of Filipino organizations. Its Family Home supports boys from extremely difficult and dangerous backgrounds, while community programs serve Mangyan indigenous families and the broader population.'
}, {
  name: 'Teach for the Philippines',
  location: 'Philippines',
  logo: 'https://cdn.magicpatterns.com/uploads/hBKFw5Z4VmXDFzi7HBerWe/Clip_path_group-1.png',
  url: 'https://www.teachforthephilippines.com/',
  description: 'Teach for the Philippines expands access to quality education by developing teachers and school leaders within the public education system. Its Fellowship places young leaders in underserved classrooms for two years, while complementary programs strengthen school leadership, community engagement, and system collaboration. The organization works to build a pipeline of educators and changemakers improving learning outcomes across schools and communities.'
}, {
  name: 'Yayasan Chow Kit',
  location: 'Malaysia',
  logo: 'https://cdn.magicpatterns.com/uploads/kccRsmJbEXxPheXu7TEfAC/Clip_path_group-3.png',
  url: 'https://yck.org.my/',
  description: "Yayasan Chow Kit in Kuala Lumpur, Malaysia, drop-in centers and a safe home for at-risk children and teenagers in one of the city's most marginalized areas. Recognized by Malaysia's authorities as a one-stop center for marginalized children, YCK provides comprehensive services including meals, case management, education, and advocacy for stateless, abandoned, and undocumented children."
}];
export function WhoWeFundPage() {
  return <div className="pb-8" data-id="element-731">
      <PageBanner title="Our Partners" subtitle="Curated selection of partners from the broader Firetree portfolio" image="https://cdn.magicpatterns.com/uploads/4imhJsDmHTb7rc8urHv2wf/Baan_Dek_Foundation_3.jpg" credit="Photo: Baan Dek Foundation" data-id="element-732" />

      {/* Child Protection Section — Full width, image edge-to-edge */}
      <section className="w-full mt-12 lg:mt-16" data-id="element-733">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch" data-id="element-734">
          <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:pl-[max(2rem,calc((100vw-80rem)/2+2rem))] py-12 lg:py-16 lg:pr-16" data-id="element-735">
            <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-736">
              Child Protection
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed mb-6 text-justify" data-id="element-737">
              Child protection is the foundation of the Firetree Fund portfolio.
              Effective protection is not a single service, nor a linear
              solution — it is an interconnected cycle, from handling immediate
              crisis through emergency social work and safe housing to building
              futures through education, vocational training, and family
              reintegration — with healthcare and legal protection available as
              needed throughout.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed mb-8 text-justify" data-id="element-738">
              Each child moves through this cycle on their own terms, with a
              tailored plan built around their specific needs and circumstances.
              This approach is also the most effective defence against
              trafficking — every element builds the resilience, options, and
              support structures that make children less vulnerable to
              exploitation before it occurs.
            </p>
            <div className="flex flex-col gap-3 mt-2" data-id="element-739">
              <Link to="/child-protection" className="inline-flex items-center px-5 py-2.5 border border-brand-red text-brand-red font-semibold hover:bg-brand-red hover:text-white transition-all duration-300 w-fit group" data-id="element-740">
                Read our perspective on child protection
                <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-741" />
              </Link>
              <button onClick={() => window.open('https://hgamykbndfrtzaqfftse.supabase.co/storage/v1/object/public/pdfs/Child%20Protection%20-%20Firetree%20Fund.pdf', '_blank')} className="inline-flex items-center px-5 py-2.5 border border-brand-charcoal text-brand-charcoal font-semibold hover:bg-brand-charcoal hover:text-white transition-all duration-300 w-fit cursor-pointer" data-id="element-742">
                Download our Child Protection Framework
                <DownloadIcon className="ml-2 w-4 h-4" data-id="element-743" />
              </button>
            </div>
          </div>

          <PhotoCredit credit="Photo: M'Lop Tapang" className="relative min-h-[400px] xl:mr-[max(2rem,calc((100vw-80rem)/2+2rem))]" data-id="element-744">
            <img src="https://cdn.magicpatterns.com/uploads/iN9iTXcEpTVauUEERd8sfa/MT_(8).jpg" alt="Child protection work in Southeast Asia" className="absolute inset-0 w-full h-full object-cover" data-id="element-745" />
          </PhotoCredit>
        </div>
      </section>

      {/* Education Innovation Section — Full width, image left */}
      <section className="w-full" data-id="element-746">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch" data-id="element-747">
          <PhotoCredit credit="Photo: Teach for the Philippines" className="relative min-h-[400px] order-2 lg:order-1 xl:ml-[max(2rem,calc((100vw-80rem)/2+2rem))]" data-id="element-748">
            <img src="https://cdn.magicpatterns.com/uploads/5S8zZcyDqYZHjTMQr2ESXY/Teach_for_the_Philippines_-_Tara_Kaya_Program.png" alt="Education innovation in the Philippines" className="absolute inset-0 w-full h-full object-cover" style={{
            objectPosition: 'center 40%'
          }} data-id="element-749" />
          </PhotoCredit>

          <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:pr-[max(2rem,calc((100vw-80rem)/2+2rem))] py-12 lg:py-16 lg:pl-16 order-1 lg:order-2" data-id="element-750">
            <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-751">
              Education Innovation
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed mb-6 text-justify" data-id="element-752">
              While child protection anchors the Fund portfolio, children cannot
              be protected effectively if the public systems meant to serve them
              are failing. Education is the primary system through which
              vulnerable children are reached, monitored, and supported daily.
              When a school functions well, teachers become the first line of
              identification and response for children at risk.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed mb-6 text-justify" data-id="element-753">
              Teach for the Philippines works inside public schools across the
              country, placing trained fellows who deliver foundational literacy
              and life skills programmes in some of the most underserved
              communities. Like our protection-focused partners, TFP embeds
              practitioners within vulnerable geographies, coordinates formally
              with government through its MOA with the Department of Education,
              and builds local capacity that outlasts any single intervention.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed text-justify" data-id="element-754">
              TFP's inclusion also reflects Firetree's direct operational
              experience in education. Through Starfish Education in Thailand,
              which Firetree fully funds and governs, we run school
              transformation programmes that develop teacher capacity and
              instructional mastery inside public schools — work that is
              structurally parallel to TFP's. As with child protection, our
              practitioner knowledge informs how we assess, support, and engage
              with TFP as a partner organisation.
            </p>
          </div>
        </div>
      </section>

      {/* Organisations Section */}
      <section className="py-24 bg-brand-card/30" data-id="element-755">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-756">
          <div className="mb-16" data-id="element-757">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-758">
              Organisations
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-759">
            {organizations.map(org => <div key={org.name} className="bg-brand-cream p-8 border border-brand-shape/50 flex flex-col h-full" data-id="element-760">
                <div className="mb-4" data-id="element-761">
                  {org.logo && <a href={org.url} target="_blank" rel="noopener noreferrer" className="mb-4 block" data-id="element-762">
                      <img src={org.logo} alt={`${org.name} logo`} className="w-auto object-contain opacity-90 hover:opacity-100 transition-opacity" style={{
                  height: '64px'
                }} data-id="element-763" />
                    </a>}
                  <h3 className="text-lg font-serif text-brand-charcoal mb-2" data-id="element-764">
                    <a href={org.url} target="_blank" rel="noopener noreferrer" className="hover:text-brand-red transition-colors" data-id="element-765">
                      {org.name}
                    </a>
                  </h3>
                  <div className="flex items-center text-sm text-brand-gold font-medium" data-id="element-766">
                    <MapPin className="w-4 h-4 mr-1" data-id="element-767" />
                    {org.location}
                  </div>
                </div>
                <div className="flex-grow" data-id="element-768">
                  <p className="text-brand-brown leading-relaxed text-justify" data-id="element-769">
                    {org.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Southeast Asia Map */}
      <section className="py-12 md:py-16 bg-brand-cream" data-id="element-770">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-771">
          <div className="text-center mb-12" data-id="element-772">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-773">
              Our presence across Southeast Asia
            </h2>
          </div>
          <SoutheastAsiaMap data-id="element-774" />
        </div>
      </section>
    </div>;
}